import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'

/**
 * URL 타입 판별
 * - relative: 상대 경로 (/로 시작)
 * - absolute: 절대 URL (http://, https://)
 * - empty: URL이 없음
 */
export type UrlType = 'relative' | 'absolute' | 'empty'

export const getUrlType = (url: string | null | undefined): UrlType => {
  if (!url) {
    return 'empty'
  }
  if (url.startsWith('/')) {
    return 'relative'
  }
  return 'absolute'
}

/**
 * URL 네비게이션 처리
 * - 상대 경로: router.push 사용 (SPA 네비게이션)
 * - 절대 URL: location.href 사용 (external=true 파라미터 추가)
 *
 * @example
 * navigateToUrl('/app/user/podo/class-report', router) // SPA 네비게이션
 * navigateToUrl('https://example.com', router) // 외부 이동 (external=true 추가)
 */
export const navigateToUrl = (url: string | null | undefined, router: AppRouterInstance) => {
  const urlType = getUrlType(url)

  switch (urlType) {
    case 'relative':
      router.push(url!)
      break
    case 'absolute': {
      const parsedUrl = new URL(url!, window.location.origin)
      parsedUrl.searchParams.set('external', 'true')
      location.href = parsedUrl.toString()
      break
    }
    case 'empty':
      break
  }
}

/**
 * 텍스트 내 ${변수명} 패턴을 실제 값으로 치환
 *
 * @param text - 치환할 텍스트 (예: "/report?id=${subscribe_mapp_id}")
 * @param variables - 변수 객체 (예: { subscribe_mapp_id: "abc123" })
 * @param wrapWithSpan - true면 치환된 값을 <span>으로 감쌈 (HTML 렌더링용)
 * @returns 치환된 텍스트 또는 null
 *
 * @example
 * formatTextWithVariables("/report?id=${id}", { id: "123" })
 * // 결과: "/report?id=123"
 *
 * formatTextWithVariables("안녕 ${name}님", { name: "홍길동" }, true)
 * // 결과: "안녕 <span>홍길동</span>님"
 */
export const formatTextWithVariables = (
  text: string | null | undefined,
  variables: Record<string, unknown> | undefined,
  wrapWithSpan = false,
): string | null => {
  if (!text) {
    return null
  }
  if (!variables) {
    return text
  }

  let formatted = text
  for (const [key, value] of Object.entries(variables)) {
    const regex = new RegExp(`\\$\\{${key}\\}`, 'g')
    const replacement = wrapWithSpan ? `<span>${value}</span>` : String(value)
    formatted = formatted.replace(regex, replacement)
  }
  return formatted
}

/**
 * 객체에서 지정된 키들만 추출하여 변수 객체 생성
 *
 * @param source - 원본 객체
 * @param keys - 추출할 키 배열
 * @returns 추출된 키-값 쌍 객체
 *
 * @example
 * const popup = { id: 1, subscribe_mapp_id: "abc", name: "test" }
 * extractVariables(popup, ['subscribe_mapp_id', 'id'])
 * // 결과: { subscribe_mapp_id: "abc", id: 1 }
 */
export const extractVariables = <T extends Record<string, unknown>>(
  source: T,
  keys: (keyof T)[],
): Record<string, unknown> => {
  return Object.fromEntries(keys.map((key) => [key, source[key]]))
}
